// Groups API service — HTTP-based replacement for groups-service.js (Firestore)
// Calls SQL-backed Cloud Function endpoints instead of Firestore SDK.
//
// Every endpoint takes the Viewer from the bearer token apiFetch attaches
// (ADR-0020), so no request names the acting uid. `targetUid` names the member
// acted ON (a role change or a removal); a leave names no member.

import { API_ENDPOINTS } from './endpoints.js';
import { apiJson } from './api-fetch.js';
import { authState } from '../stores/auth.svelte.js';

// ============================================================
// Helpers
// ============================================================

/** @returns {import('../types.js').Subgroup[]} */
function mapSubgroups(list) {
  return (list || []).map((s) => ({
    groupId: s.groupId,
    label: s.label,
    memberCount: Number(s.memberCount) || 0,
    isMember: s.isMember === true,
  }));
}

// ============================================================
// Group CRUD
// ============================================================

/**
 * Create a new group.
 * @param {{ name: string, description?: string }} groupInfo
 * @returns {Promise<string>} The new groupId
 */
export async function createGroup({ name, description = '' }) {
  const data = await apiJson(API_ENDPOINTS.groups_api, {
    method: 'POST',
    json: { name: name.trim(), description: description.trim() },
  });
  return data.groupId;
}

/**
 * Get a group document by ID, with members and invites.
 * @param {string} groupId
 * @returns {Promise<{ group: Object, members: Array, invites: Array, subgroups: import('../types.js').Subgroup[] }>}
 */
export async function getGroupDetail(groupId) {
  const data = await apiJson(`${API_ENDPOINTS.groups_api}?groupId=${encodeURIComponent(groupId)}`);
  // Map SQL snake_case to camelCase for frontend components
  const members = (data.members || []).map((m) => ({
    uid: m.uid,
    role: m.role,
    displayName: m.display_name || m.displayName || null,
    photoURL: m.photo_url || m.photoURL || null,
    joinedAt: m.joined_at || m.joinedAt || null,
    subgroupLabels: m.subgroup_labels || [],
    // One entry per Claim; shared = false only on the viewer's own Claims
    // where they are dark in this server ("not shared here").
    claimedBinders: (m.claimed_binders || []).map((b) => ({
      url: b.url,
      name: b.name,
      owner: b.owner,
      shared: b.shared !== false,
    })),
  }));
  const invites = (data.invites || []).map((i) => ({
    token: i.token,
    groupId: i.group_id || i.groupId,
    shortCode: i.short_code || i.shortCode || null,
    createdBy: i.created_by || i.createdBy,
    createdAt: i.created_at || i.createdAt,
    expiresAt: i.expires_at || i.expiresAt || null,
    active: i.active,
  }));
  const group = data.group
    ? {
        id: data.group.id,
        name: data.group.name,
        description: data.group.description,
        iconUrl: data.group.icon_url || null,
        createdBy: data.group.created_by || data.group.createdBy,
        createdAt: data.group.created_at || data.group.createdAt,
        updatedAt: data.group.updated_at || data.group.updatedAt,
      }
    : null;
  return { group, members, invites, subgroups: mapSubgroups(data.subgroups) };
}

/**
 * Get a group document by ID. Returns null if not found.
 * @param {string} groupId
 * @returns {Promise<Object|null>}
 */
export async function getGroup(groupId) {
  try {
    const { group } = await getGroupDetail(groupId);
    return group;
  } catch {
    return null;
  }
}

/**
 * Update group metadata (name, description).
 * @param {string} groupId
 * @param {{ name?: string, description?: string }} updates
 */
export async function updateGroup(groupId, updates) {
  await apiJson(API_ENDPOINTS.groups_api, {
    method: 'PATCH',
    json: { groupId, ...updates },
  });
  console.log('[Groups] Updated group:', groupId);
}

// ============================================================
// Member Management
// ============================================================

/**
 * Get all members of a group.
 * @param {string} groupId
 * @returns {Promise<Array>}
 */
export async function getGroupMembers(groupId) {
  const { members } = await getGroupDetail(groupId);
  return members;
}

/**
 * Update a member's role.
 * @param {string} groupId
 * @param {string} targetUid
 * @param {'admin' | 'member'} newRole
 */
export async function updateMemberRole(groupId, targetUid, newRole) {
  await apiJson(API_ENDPOINTS.group_members_api, {
    method: 'PATCH',
    json: { groupId, targetUid, newRole },
  });
  console.log('[Groups] Updated role for', targetUid, 'to', newRole);
}

/**
 * Remove a member from a group.
 * @param {string} groupId
 * @param {string} targetUid
 */
export async function removeMember(groupId, targetUid) {
  await apiJson(API_ENDPOINTS.group_members_api, {
    method: 'DELETE',
    json: { groupId, targetUid },
  });
  console.log('[Groups] Removed member:', targetUid);
}

/**
 * Leave a group (current user removes themselves).
 * @param {string} groupId
 */
export async function leaveGroup(groupId) {
  // No targetUid: the server reads a DELETE naming no member as the Viewer
  // (the token's uid) leaving.
  await apiJson(API_ENDPOINTS.group_members_api, {
    method: 'DELETE',
    json: { groupId },
  });
  console.log('[Groups] Left group:', groupId);
}

// ============================================================
// Invite Links
// ============================================================

/**
 * Create a multi-use invite link for a group.
 * @param {string} groupId
 * @param {{ expiresInDays?: number | null }} options
 * @returns {Promise<string>} The short code
 */
export async function createInviteLink(groupId, { expiresInDays = null } = {}) {
  const data = await apiJson(API_ENDPOINTS.group_invites_api, {
    method: 'POST',
    json: { groupId, expiresInDays },
  });
  console.log('[Groups] Created invite for group:', groupId, 'shortCode:', data.shortCode);
  return data.shortCode;
}

/**
 * Revoke an invite link.
 * @param {string} groupId
 * @param {string} token
 */
export async function revokeInviteLink(groupId, token) {
  await apiJson(API_ENDPOINTS.group_invites_api, {
    method: 'DELETE',
    json: { groupId, token },
  });
  console.log('[Groups] Revoked invite:', token);
}

/**
 * Get all active invite links for a group.
 * @param {string} groupId
 * @returns {Promise<Array>}
 */
export async function getGroupInvites(groupId) {
  const { invites } = await getGroupDetail(groupId);
  return invites;
}

/**
 * Get invite info by groupId and token (for direct invite links).
 * @param {string} groupId
 * @param {string} token
 * @returns {Promise<{ invite: Object, group: Object|null, groupId: string, token: string } | null>}
 */
export async function getInviteInfo(groupId, token) {
  try {
    const data = await apiJson(
      `${API_ENDPOINTS.group_invites_api}?groupId=${encodeURIComponent(groupId)}&token=${encodeURIComponent(token)}`
    );
    return { invite: data.invite, group: data.group, groupId: data.groupId, token: data.token };
  } catch {
    return null;
  }
}

/**
 * Resolve a short code to invite info.
 * @param {string} shortCode
 * @returns {Promise<{ invite: Object, group: Object|null, groupId: string, token: string } | null>}
 */
export async function getInviteInfoByShortCode(shortCode) {
  try {
    const data = await apiJson(
      `${API_ENDPOINTS.group_invites_api}?shortCode=${encodeURIComponent(shortCode)}`
    );
    return { invite: data.invite, group: data.group, groupId: data.groupId, token: data.token };
  } catch {
    return null;
  }
}

// ============================================================
// Joining
// ============================================================

/**
 * Join a group using an invite token.
 * @param {string} groupId
 * @param {string} token
 * @returns {Promise<{ success: boolean, groupName: string, alreadyMember?: boolean }>}
 */
export async function joinGroupWithToken(groupId, token) {
  const user = authState.user;
  const data = await apiJson(API_ENDPOINTS.group_members_api, {
    method: 'POST',
    json: {
      groupId,
      token,
      displayName: user?.displayName || null,
      photoURL: user?.photoURL || null,
    },
  });
  return data;
}

// ============================================================
// Profile Refresh
// ============================================================

/**
 * Refresh the calling user's display_name and photo_url across every
 * group they're a member of. Idempotent — safe to call on every sign-in.
 *
 * Why: group_members.photo_url / display_name are snapshots taken at join
 * time. Discord avatar URLs embed a hash that rotates whenever the user
 * updates their picture, so stored URLs go stale. Calling this on sign-in
 * keeps each user's avatar fresh wherever they appear.
 *
 * The server takes the uid from the verified ID token, never from the body.
 *
 * @param {{ displayName?: string|null, photoURL?: string|null }} profile
 * @returns {Promise<{ updated: number }>}
 */
export async function refreshProfile({ displayName = null, photoURL = null } = {}) {
  if (displayName == null && photoURL == null) return { updated: 0 };
  const data = await apiJson(API_ENDPOINTS.refresh_profile_api, {
    method: 'PUT',
    json: { displayName, photoURL },
  });
  return { updated: data.updated || 0 };
}

// ============================================================
// User's Groups
// ============================================================

/**
 * Get all groups the current user belongs to.
 * @returns {Promise<import('../types.js').UserGroup[]>}
 */
export async function getUserGroups() {
  const data = await apiJson(API_ENDPOINTS.groups_api);
  // Map SQL column names to match existing frontend expectations
  return (data.groups || []).map((g) => ({
    groupId: g.id,
    name: g.name,
    description: g.description,
    iconUrl: g.icon_url || null,
    role: g.role,
    joinedAt: g.joined_at,
    createdBy: g.created_by,
    createdAt: g.created_at,
    updatedAt: g.updated_at,
    subgroups: mapSubgroups(g.subgroups),
  }));
}

// ============================================================
// Sharing Settings (Server Exclusion)
// ============================================================

/**
 * Get the caller's settings: their Groups with Server Exclusion state, plus the
 * global Want Alert preference.
 *
 * `groups` is the owner's management view: EVERY Group the user belongs to
 * (including excluded ones and web-only Groups), not just the ones currently
 * sharing. `shareBinders === true` means the owner's binders are shared with
 * that Group (the default); `false` means excluded. See ADR-0006.
 *
 * `wantAlertsEnabled` is the global Want Alert preference (ADR-0007), or null
 * when the caller has no linked Discord account. `discordLinked` reports whether
 * a Discord account is linked.
 *
 * @returns {Promise<{
 *   groups: Array<{ groupId: string, name: string, iconUrl: string|null, role: string, shareBinders: boolean }>,
 *   wantAlertsEnabled: boolean|null,
 *   discordLinked: boolean
 * }>}
 */
export async function getSharingSettings() {
  const data = await apiJson(API_ENDPOINTS.sharing_settings_api);
  return {
    groups: (data.groups || []).map((g) => ({
      groupId: g.groupId,
      name: g.name,
      iconUrl: g.iconUrl || null,
      role: g.role,
      shareBinders: g.shareBinders !== false,
    })),
    wantAlertsEnabled: data.wantAlertsEnabled ?? null,
    discordLinked: data.discordLinked === true,
  };
}

/**
 * Set the Server Exclusion flag for one of the caller's Groups.
 *
 * @param {string} groupId
 * @param {boolean} shareBinders - true = share (default), false = exclude.
 */
export async function setSharingForGroup(groupId, shareBinders) {
  await apiJson(API_ENDPOINTS.sharing_settings_api, {
    method: 'PATCH',
    json: { groupId, shareBinders },
  });
  console.log('[Groups] Set share_binders for', groupId, '=', shareBinders);
}

/**
 * Set the caller's global Want Alert preference (ADR-0007).
 *
 * @param {boolean} enabled - true = receive Want Alert DMs (default), false = off.
 */
export async function setWantAlertsPreference(enabled) {
  await apiJson(API_ENDPOINTS.sharing_settings_api, {
    method: 'PATCH',
    json: { wantAlertsEnabled: enabled },
  });
  console.log('[Groups] Set want_alerts_enabled =', enabled);
}

// ============================================================
// Group Binders
// ============================================================

/**
 * The group's visible Claims, one row per Claim (two Claimants of one binder
 * are two rows), optionally scoped to a subgroup selection (web subgroup
 * picker spec). Rows: claimant_uid, binder_id, url, name, claimed_at,
 * shared, is_viewer, member_name, owner.
 * @param {string} groupId
 * @param {string[]} [subgroupIds]
 */
export async function getGroupBinders(groupId, subgroupIds = []) {
  const sp = new URLSearchParams({ groupId, binders: 'true' });
  if (subgroupIds.length > 0) sp.set('subgroupIds', subgroupIds.join(','));
  const data = await apiJson(`${API_ENDPOINTS.groups_api}?${sp.toString()}`);
  return data.binders || [];
}

// ============================================================
// Claimed Binders
// ============================================================

/**
 * Get claimed binders for the current user.
 * @returns {Promise<Array<{ url: string, name: string, owner: string }>>}
 */
export async function getClaimedBinders() {
  const data = await apiJson(API_ENDPOINTS.claimed_binders_api);
  return data.binders || [];
}

/**
 * Save claimed binders for the current user (replaces all).
 * @param {Array<{ url: string, name: string, owner: string }>} binders
 */
export async function saveClaimedBinders(binders) {
  await apiJson(API_ENDPOINTS.claimed_binders_api, {
    method: 'PUT',
    json: { binders },
  });
}

// ============================================================
// Pinned Binders
// ============================================================

/**
 * Get pinned binder URLs for the current user.
 * @returns {Promise<string[]>}
 */
export async function getPinnedBindersFromApi() {
  const data = await apiJson(API_ENDPOINTS.pinned_binders_api);
  return data.urls || [];
}

/**
 * Save pinned binder URLs for the current user (replaces all).
 * @param {string[]} urls
 */
export async function savePinnedBindersToApi(urls) {
  await apiJson(API_ENDPOINTS.pinned_binders_api, {
    method: 'PUT',
    json: { urls },
  });
}
